// Global Instructions Rule Applied!
//
// HTTP calls of the Interest Accruals workbench (docs/INTEREST_ACCRUAL_DESIGN.md
// section 6.1). Every response is validated with its Zod schema before it
// reaches the component; a failure comes back as { data: null, error } with
// the backend's own sentence (a refusal of the booking engine reads as the
// RPC's RAISE message).
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  InterestAccrualPosition,
  InterestRun,
  InterestRunActionResult,
  InterestRunDetail,
  InterestSide,
  validateInterestAccrualPosition,
  validateInterestRunActionResult,
  validateInterestRunDetail,
  validateInterestRuns
} from '../../../state_manager/data_models/interest_accrual_position.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface ServiceResult<T> {
  data: T | null
  error?: string
}

/** A receivable line: the amount to book in the SECURITY currency. */
export interface ReceivableRunLine {
  tranche_id: string
  amount: number
  override: boolean
  // set = the transaction books at this rate; null = the FX feed fills it
  fx_rate: number | null
}

/** A payable line: one per loan; a null override books the calculated figure. */
export interface PayableRunLine {
  fund_loan_id: string
  interest_override: number | null
  charge_override: number | null
}

export type CreateInterestRunPayload =
  | { fund_id: string; as_of: string; side: 'receivable'; lines: ReceivableRunLine[]; notes: string | null }
  | { fund_id: string; as_of: string; side: 'payable'; lines: PayableRunLine[]; notes: string | null }

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

interface BackendEnvelope {
  success?: boolean
  data?: unknown
  error?: string
  message?: string
}

// One request: the backend's sentence on a failure, the validated data on
// success.
async function request<T>(
  url: string,
  init: RequestInit,
  validate: (data: unknown) => T,
  fallbackError: string
): Promise<ServiceResult<T>> {
  try {
    const response = await fetch(url, init)
    const json: BackendEnvelope = await response.json().catch(() => ({}))
    if (!response.ok || json.success === false) {
      if (response.status === 401) return { data: null, error: 'Authentication failed - please log in again' }
      return { data: null, error: json.error || json.message || `${fallbackError}: ${response.status}` }
    }
    return { data: validate(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error(`${fallbackError}:`, error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchInterestAccrualPosition = (
  fundId: string,
  asOf: string,
  side: InterestSide,
  accessToken: string | null = null
): Promise<ServiceResult<InterestAccrualPosition>> =>
  request(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTEREST_ACCRUALS.POSITION, { fundId }, { as_of: asOf, side }),
    { method: API_ROUTES.INTEREST_ACCRUALS.POSITION.method, headers: authHeaders(accessToken) },
    (data) => validateInterestAccrualPosition(side, data ?? {}),
    'Failed to compute the interest accrual position'
  )

export const fetchInterestRuns = (
  fundId: string,
  side: InterestSide | null = null,
  accessToken: string | null = null
): Promise<ServiceResult<InterestRun[]>> =>
  request(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTEREST_ACCRUALS.RUNS, { fundId }, side ? { side } : {}),
    { method: API_ROUTES.INTEREST_ACCRUALS.RUNS.method, headers: authHeaders(accessToken) },
    (data) => validateInterestRuns(data ?? []),
    'Failed to fetch interest runs'
  )

export const fetchInterestRunDetail = (
  runId: string,
  accessToken: string | null = null
): Promise<ServiceResult<InterestRunDetail>> =>
  request(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTEREST_ACCRUALS.RUN, { id: runId }),
    { method: API_ROUTES.INTEREST_ACCRUALS.RUN.method, headers: authHeaders(accessToken) },
    (data) => validateInterestRunDetail(data ?? {}),
    'Failed to fetch the interest run'
  )

/** Create and book one run - atomic: a refusal writes nothing. */
export const createInterestRun = (
  payload: CreateInterestRunPayload,
  accessToken: string | null = null
): Promise<ServiceResult<InterestRunDetail>> =>
  request(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTEREST_ACCRUALS.CREATE_RUN),
    {
      method: API_ROUTES.INTEREST_ACCRUALS.CREATE_RUN.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    },
    (data) => validateInterestRunDetail(data ?? {}),
    'Failed to book the interest run'
  )

export const bookInterestRun = (
  runId: string,
  accessToken: string | null = null
): Promise<ServiceResult<InterestRunDetail>> =>
  request(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTEREST_ACCRUALS.BOOK_RUN, { id: runId }),
    { method: API_ROUTES.INTEREST_ACCRUALS.BOOK_RUN.method, headers: authHeaders(accessToken) },
    (data) => validateInterestRunDetail(data ?? {}),
    'Failed to book the interest run'
  )

/** dryRun = only check and count (the confirm dialog), nothing changes. */
export const unbookInterestRun = (
  runId: string,
  dryRun: boolean,
  accessToken: string | null = null
): Promise<ServiceResult<InterestRunActionResult>> =>
  request(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTEREST_ACCRUALS.UNBOOK_RUN, { id: runId }),
    {
      method: API_ROUTES.INTEREST_ACCRUALS.UNBOOK_RUN.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { dry_run: dryRun } })
    },
    (data) => validateInterestRunActionResult(data ?? {}),
    'Failed to unbook the interest run'
  )

export const deleteInterestRun = (
  runId: string,
  accessToken: string | null = null
): Promise<ServiceResult<InterestRunActionResult>> =>
  request(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTEREST_ACCRUALS.DELETE_RUN, { id: runId }),
    { method: API_ROUTES.INTEREST_ACCRUALS.DELETE_RUN.method, headers: authHeaders(accessToken) },
    (data) => validateInterestRunActionResult(data ?? {}),
    'Failed to delete the interest run'
  )
